import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { OPEN_SECTION_EVENT } from './helpers';

// Shared building blocks for the single-page create order flow.

const isCompactScreen = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;

// Collapsible section. Content stays mounted while collapsed so its data loading keeps running.
// On mobile/tablet a section folds itself up the first time it becomes complete, and re-opens if it needs attention.
export function Section({ id, step, title, subtitle, summary, done, disabled, action, children }) {
  const [open, setOpen] = useState(true);
  const autoCollapsedRef = useRef(false);

  useEffect(() => {
    if (done && !autoCollapsedRef.current && isCompactScreen()) {
      autoCollapsedRef.current = true;
      setOpen(false);
    } else if (!done && !disabled) setOpen(true);
  }, [done, disabled]);

  // "Still needed" links and the mobile bar ask a section to open before scrolling to it.
  useEffect(() => {
    const onOpen = (e) => e.detail === id && setOpen(true);
    window.addEventListener(OPEN_SECTION_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SECTION_EVENT, onOpen);
  }, [id]);

  const line = open ? subtitle : summary || subtitle;

  return (
    <section
      id={id}
      className={`scroll-mt-4 rounded-lg border bg-white transition ${
        disabled ? 'border-slate-200/70' : 'border-slate-200 shadow-sm'
      }`}
    >
      <header className={`flex items-center gap-3 px-4 sm:px-5 ${open ? 'pt-4' : 'py-3.5'}`}>
        <button
          type='button'
          disabled={disabled}
          aria-expanded={open}
          aria-controls={`${id}-body`}
          onClick={() => setOpen((v) => !v)}
          className='flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default'
        >
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition ${
              done ? 'bg-green-500 text-white' : disabled ? 'bg-slate-100 text-slate-400' : 'bg-client-300 text-white'
            }`}
          >
            {done ? <CheckIcon className='h-4 w-4' /> : step}
          </span>
          <span className='min-w-0 flex-1'>
            <span
              className={`block text-[15px] font-semibold leading-tight ${
                disabled ? 'text-slate-400' : 'text-slate-900'
              }`}
            >
              {title}
            </span>
            {line && (
              <span className={`mt-0.5 block truncate text-xs ${open ? 'text-slate-500' : 'text-slate-600'}`}>
                {line}
              </span>
            )}
          </span>
        </button>
        {open && action}
        {!disabled && (
          <button
            type='button'
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
            onClick={() => setOpen((v) => !v)}
            className='-mr-1 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
          >
            <ChevronDownIcon className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </header>
      <div id={`${id}-body`} className={open ? 'px-4 pb-4 pt-3 sm:px-5 sm:pb-5' : 'hidden'}>
        {children}
      </div>
    </section>
  );
}

export function Placeholder({ children }) {
  return (
    <div className='rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-4 py-6 text-center text-sm text-slate-400'>
      {children}
    </div>
  );
}

export function LinkButton({ children, onClick, className = '', ...rest }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`text-client-300 inline-flex items-center gap-1 text-xs font-semibold hover:underline disabled:opacity-50 ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// Horizontal, scrollable row of compact date pills. dates: [{ key, weekday, day, full }]
export function DateStrip({ dates, value, onChange }) {
  return (
    <div className='-mx-1 flex gap-1.5 overflow-x-auto px-1 py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
      {dates.map((d) => {
        const active = d.key === value;
        return (
          <button
            key={d.key}
            type='button'
            title={d.full}
            onClick={() => onChange(d.key)}
            aria-pressed={active}
            className={`w-12 shrink-0 rounded-md py-1.5 text-center transition ${
              active ? 'bg-client-300 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <span className={`block text-[10px] font-medium uppercase ${active ? 'text-white/80' : 'text-slate-400'}`}>
              {d.weekday}
            </span>
            <span className='block text-base font-semibold leading-tight'>{d.day}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Spinner({ className = 'h-4 w-4' }) {
  return (
    <svg className={`animate-spin text-slate-400 ${className}`} viewBox='0 0 24 24' fill='none'>
      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z' />
    </svg>
  );
}

export function LoadingRow({ text }) {
  return (
    <div className='flex items-center justify-center gap-2 py-6 text-sm text-slate-400'>
      <Spinner />
      {text}
    </div>
  );
}
